// Wires chat realtime to root state. Called once from pages/index.js onCreate
// (`el._chatChannel = el.call('subscribeRealtime')`); cancelled in onRemove.
//
// ── Transport: server SSE (GET /core/chat/stream) ───────────────────────────
// Chat realtime rides the server SSE stream via sdk.realtime.subscribeChatSse
// (_openChatSse) — the canonical chat transport since the 2026-06-18 flip
// (server 72d30f52 + workspace 643b2be8). The SSE re-framer reconstructs the
// Supabase-style `{ eventType, new, old, _kind? }` envelope the shared handler
// bodies (_handleMessageInsert, _handleChannels, …) consume, so the downstream
// consumers (root.messagesByChannel, root.channels, …) update the same way.
//
// The legacy Supabase postgres_changes channel (the old rollback transport +
// its presence track/sync) was RETIRED with the governance Supabase project,
// so there is no longer a transport switch — SSE is the sole path. Presence is
// a separate socket-layer follow-up (spec §4) and is NOT carried on this stream.
import {
  rowToMessage,
  rowToChannel,
  getActiveWorkspaceId,
  scopeMembersToActiveWorkspace,
  setChannelMessages,
  isBroadcastMention,
  isChatChannelInView
} from './chatUtils.js'

import { createNotification } from '@symbo-ls/workspace-shared/functions/notifications/createNotification.js'
import { isChannelMuted } from './muteChannel.js'
import { chatTypingApply, chatTypingClear, chatTypingPrune } from './chatTyping.js'
import { getSDK } from '@symbo-ls/workspace-shared/functions/sdk.js'
// A realtime row for workspace A's channel can arrive, trigger a masked-body
// refetch, and resolve after the user switched. Everything the two handlers
// below write past that await is workspace-scoped, so each captures the
// workspace id first — see functions/sameWorkspaceWriter.js.
import { wsIdOf } from './sameWorkspaceWriter.js'

// Realtime workspace gate — STRICTER than chatUtils.rowInActiveWorkspace:
// root.channels is scoped at state-write time, so a row whose channel_id is
// absent from state belongs to another workspace (or predates hydration) and
// is DROPPED — the page's fetch declarations reconcile any early gap. Rows
// carrying their own workspace_id are matched directly.
//
// This IS the RECEIVER-side guard tickets/workspace.md's P1 (2026-08-08,
// "defence in depth") asks every realtime surface to have — chat already had
// it, and correctly: it compares against getActiveWorkspaceId(root), which
// prefers root.workspace.id (this tab's own resolved workspace) over the
// shared pointer, so it isn't a no-op even when localStorage.activeWorkspace
// is shared/poisoned across tabs. Exported (alongside `dispatch`'s call
// sites, unchanged) purely so subscribeRealtime.test.js can lock this
// behavior directly — see subscribeTicketsRealtime.js / subscribeMeetRealtime.js
// for the sibling receivers this same P1 pass added guards/tests to.
export const _chatInScope = (root, row) => {
  if (!row) return false
  const activeWs = getActiveWorkspaceId(root)
  if (!activeWs) return true
  const ws = row.workspace_id
  if (ws) return ws === activeWs
  const chId = row.channel_id
  if (!chId) return true
  return (root.channels || []).some((c) => c.id === chId)
}

// Refetch a single chat_messages row via the SDK's curated handler when the
// realtime payload arrives with masked columns. The server SSE stream masks
// per-subscriber CORRECTLY (service-role-complete rows, guard-scoped — §3), so
// these refetch/skip branches are dead on the SSE transport; they stay as
// defensive no-ops (rows always arrive complete there). See SERVER-CHAT-AUTH-1.
const _refetchMessage = async (channelId, messageId) => {
  try {
    const sdk = getSDK()
    if (!sdk?.execute) return null
    const res = await sdk.execute('workspaceProject.chat.messages', 'list', {
      channelId,
      limit: 1,
      params: { id: messageId }
    })
    const rows = res?.data || res
    const row = Array.isArray(rows)
      ? rows.find((r) => String(r.id) === String(messageId)) || rows[0]
      : rows
    return row && typeof row === 'object' && row.body !== undefined ? row : null
  } catch (_) {
    return null
  }
}

// ── Shared handler bodies (transport-agnostic) ──────────────────────────────
// Each takes the live `root` + a Supabase-style payload `{ eventType, new, old }`
// (the SSE re-framer reconstructs the SAME shape, spec §5.2) and mutates root
// state. `me` is the current user email.

// Toast + chime for an incoming message — mirrors the mention-notification
// mute gate (isChannelMuted, kept consistent with _handleMentionInsert below)
// and skips the viewer's own echo. Also skipped when the conversation is
// ALREADY visible (active channel + the messenger/chat surface actually in
// view + the tab focused) — the message bubble appearing in place is
// feedback enough; a toast on top of it would be redundant chrome.
// `el` is the element `subscribeRealtime` was called on (threaded through
// `dispatch` below) — in practice the shell root, since app.js's onCreate
// opens this subscription before any /chat visit, so el.call resolves
// against the always-mounted shell context regardless of what page the
// viewer is currently on.
const _notifyNewMessage = (root, msg, el) => {
  if (!el || typeof el.call !== 'function') return
  if (!msg?.channelId) return
  if (msg.authorEmail && msg.authorEmail === root.currentUserEmail) return
  if (isChannelMuted(root, msg.channelId)) return
  if (isChatChannelInView(root, msg.channelId)) return

  const channel = (root.channels || []).find((c) => c.id === msg.channelId)
  // chatFindPerson also resolves a peer a non-manager's /people rows list
  // without email (through the chat member rows' user_id).
  const person = msg.authorEmail ? el.call('chatFindPerson', root, msg.authorEmail) : null
  const senderName = person?.name || (msg.authorEmail || '').split('@')[0] || 'Someone'
  const channelSuffix =
    channel && channel.kind === 'channel' && channel.name ? ` · #${channel.name}` : ''
  // The composer stores typed `*_~`[]` as `\*` etc. (htmlToMarkdown) — show
  // them as typed, not as backslashes.
  const snippet = (msg.body || '')
    .replace(/\\([*_~`[\]\\])/g, '$1')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 90)

  try {
    // A message that carries only a file has no text: its line is the chat
    // word for an attachment (the fallback functions/chatAnswer.js uses), never
    // the file name and never an empty line (CL-CHAT-GAPS item 5).
    const hasFile = Array.isArray(msg.attachments) && msg.attachments.length > 0
    const line =
      snippet || (hasFile ? String(el.call('polyglot', 'chat.answer.attachment') || '') : '')
    el.call('openNotification', {
      title: senderName + channelSuffix,
      message: line,
      icon: 'chat',
      sound: true,
      action: { call: 'openChatFromToast', args: [msg.channelId] }
    })
  } catch (err) {
    console.warn('[chat-rt] message toast failed:', err?.message || err)
  }
}

// Broadcast mentions (@all/@everyone) never produce a chat_mentions DB row —
// the server-side chat_extract_mentions trigger only matches @handles against
// real member emails (sendMessage.js's notification fan-out is the
// cross-app-bell equivalent for this same case, fired once by the author's
// client). This is the in-chat counterpart: EVERY member's own connected
// client evaluates every incoming message for itself and, if broadcast +
// not-the-author + still-a-member, synthesizes a myMentions row — so the
// sidebar MentionBadge, the "@ Mentions" jump action, and markMentionsRead
// all key off it exactly like a real per-user mention (mute-independent,
// mirroring _handleMentionInsert's own myMentions push above). Doesn't
// survive a reload — no DB row backs it, and the initial
// workspaceProject.chat.mentions bulk fetch can't see it either — only
// live/connected recipients get it.
const _handleBroadcastMention = (root, msg) => {
  const me = root.currentUserEmail
  if (!msg || !msg.channelId || !msg.id || !me) return
  if (msg.authorEmail === me) return
  if (!isBroadcastMention(msg.body)) return
  const isMember = (root.channelMembers || []).some(
    (r) => r.channel_id === msg.channelId && r.member_email === me
  )
  if (!isMember) return
  const cur = root.myMentions || []
  const syntheticId = `bcast:${msg.id}`
  if (cur.some((x) => x.id === syntheticId)) return
  root.update({
    myMentions: [
      ...cur,
      {
        id: syntheticId,
        channel_id: msg.channelId,
        message_id: msg.id,
        mentioned_email: me,
        read_at: null,
        _broadcast: true
      }
    ]
  })
}

const _handleMessageInsert = async (root, payload, el) => {
  const _issuedFor = wsIdOf(root)
  let msg = rowToMessage(payload.new)
  const list = root.messagesByChannel[msg.channelId] || []
  // Skip if we already have it (optimistic id was reconciled in sendMessage).
  if (list.some((m) => m.id === msg.id)) return
  // Masked realtime payload: row arrives with `body` undefined. The curated
  // handler is service-role and returns the body. Refetch before rendering so
  // we don't show an empty bubble. (Dead on SSE — rows always arrive complete
  // there.) See SERVER-CHAT-AUTH-1.
  if (msg.body === undefined && msg.channelId && msg.id) {
    const full = await _refetchMessage(msg.channelId, msg.id)
    if (full) msg = rowToMessage(full)
    // If refetch still failed (genuinely no access), drop the event rather than
    // render an empty bubble.
    if (msg.body === undefined) return
  }
  // The whole tail is dropped, not just the message write: `myMentions` is
  // workspace-scoped too, and a toast about workspace A's channel is wrong in
  // workspace B even before it is clicked (its click writes activeChannelId).
  if (wsIdOf(root) !== _issuedFor) return
  // `_live`: this row reached the channel by realtime, not by a history read.
  // In a channel nobody has read yet it is the entry's only row, and the
  // channel's history must still be read when it opens (chatUtils
  // hasChannelHistory, functions/loadChannelMessages.js).
  setChannelMessages(root, msg.channelId, [...list, { ...msg, _live: true }])
  // The author's message ends their "is typing" line (CHAT-TYPING-1).
  const typing = chatTypingClear(root, msg.channelId, msg.authorEmail)
  if (typing) root.update({ _chatTypists: typing })
  _handleBroadcastMention(root, msg)
  _notifyNewMessage(root, msg, el)
}

// "Is typing" rows (CHAT-TYPING-1). The stream masks them like messages — a
// private channel's typing reaches only its members — so here they only pass
// the workspace gate (dispatch). A `start` arms the person's entry for 6 s; a
// prune just after that drops whatever expired, so a lost `stop` (a closed
// tab, a dropped network) clears on its own.
export const _handleTyping = (root, payload) => {
  const next = chatTypingApply(root, payload && payload.new)
  if (!next) return
  root.update({ _chatTypists: next })
  setTimeout(() => {
    const pruned = chatTypingPrune(root)
    if (pruned) root.update({ _chatTypists: pruned })
  }, 6100)
}

// A message deleted elsewhere (its author on another screen, a channel admin).
// The SDK hands DELETE frames over as { eventType: 'DELETE', new: null,
// old: { id, channel_id } }; dispatch used to scope-check `payload.new` —
// null on every delete — so the row stayed on every other screen until a
// reload. Removes the row (and closes its thread if that thread is open).
export const _handleMessageDelete = (root, payload) => {
  const old = payload && payload.old
  if (!root || !old || old.id == null) return
  const id = String(old.id)
  const map = root.messagesByChannel || {}
  const chIds = old.channel_id != null ? [String(old.channel_id)] : Object.keys(map)
  for (const chId of chIds) {
    const list = map[chId]
    if (!Array.isArray(list) || !list.some((m) => m && String(m.id) === id)) continue
    setChannelMessages(
      root,
      chId,
      list.filter((m) => m && String(m.id) !== id)
    )
  }
  if (root.activeThreadRootId != null && String(root.activeThreadRootId) === id) {
    root.update({ activeThreadRootId: null })
  }
}

const _handleMessageUpdate = async (root, payload) => {
  const _issuedFor = wsIdOf(root)
  let msg = rowToMessage(payload.new)
  if (msg.body === undefined && msg.channelId && msg.id) {
    const full = await _refetchMessage(msg.channelId, msg.id)
    if (full) msg = rowToMessage(full)
    // Don't overwrite a present-body local copy with a body-less realtime
    // payload — keep the locally-rendered text.
    if (msg.body === undefined) return
  }
  // A channel nobody has read has nothing to update. Mapping over `|| []`
  // wrote `[]` here, which MessageList reads as "loaded and empty" — a
  // "No messages yet" for a channel with history (and a skipped history
  // read, before loadChannelMessages re-validated `[]`).
  const current = (root.messagesByChannel || {})[msg.channelId]
  if (!Array.isArray(current)) return
  // An updated row keeps the provenance it had (`_live`, chatUtils
  // hasChannelHistory): an edit or a reaction does not turn a streamed row
  // into history, nor history into a streamed row.
  const list = current.map((m) => (m.id === msg.id ? (m._live ? { ...msg, _live: true } : msg) : m))
  setChannelMessages(root, msg.channelId, list, _issuedFor)
}

const _handleChannels = (root, payload) => {
  const next = rowToChannel(payload.new || payload.old)
  // Masked payload — primary key blank means we can't safely operate on this
  // event. Skip rather than append `{id: undefined}` to root.channels.
  if (!next.id) return
  // Read live state — `root.channels` from the closure may be stale.
  const current = root.channels || []
  const exists = current.find((c) => c.id === next.id)
  let channels = current
  if (payload.eventType === 'DELETE') {
    channels = current.filter((c) => c.id !== next.id)
  } else if (exists) {
    channels = current.map((c) => (c.id === next.id ? next : c))
  } else {
    channels = [...current, next]
  }
  // Only update if the array actually changed.
  if (channels !== current) root.update({ channels })
}

const _handleMembers = (root, payload) => {
  // Apply the delta in-place — full refetch on every membership flip (e.g.
  // every `markRead` write) was downloading the entire join table.
  const cur = root.channelMembers || []
  const isSameRow = (a, b) => a.channel_id === b.channel_id && a.member_email === b.member_email
  // Masked payload — without the composite key we can't dedupe, delete, or
  // update. Skip these events; the next page-level fetch (cache: '30s' on the
  // chat page's chat.members fetch decl) will reconcile.
  const probe = payload.new || payload.old
  if (!probe?.channel_id || !probe?.member_email) return
  let next = cur
  if (payload.eventType === 'INSERT') {
    if (!cur.some((r) => isSameRow(r, payload.new))) next = [...cur, payload.new]
  } else if (payload.eventType === 'UPDATE') {
    next = cur.map((r) => (isSameRow(r, payload.new) ? payload.new : r))
  } else if (payload.eventType === 'DELETE') {
    next = cur.filter((r) => !isSameRow(r, payload.old))
  }
  if (next !== cur) root.update({ channelMembers: next })
}

// `el` is threaded in for the same reason _handleMessageInsert takes it: the
// notification title is a polyglot key that only an element can resolve.
const _handleMentionInsert = (root, payload, me, el) => {
  const row = payload.new
  if (!row || row.mentioned_email !== me) return
  const cur = root.myMentions || []
  if (cur.some((x) => x.id === row.id)) return
  root.update({ myMentions: [...cur, row] })
  // Skip notification if the viewer muted this conversation.
  if (isChannelMuted(root, row.channel_id)) return
  // Create a notification for the mention
  const msg = (root.messagesByChannel || {})[row.channel_id]?.find((m) => m.id === row.message_id)
  const senderName = msg?.authorName || msg?.author_email || 'Someone'
  const channelName =
    (root.channels || []).find((c) => c.id === row.channel_id)?.name || 'a channel'
  createNotification.call(
    { state: { root } },
    {
      userEmail: me,
      type: 'mention',
      title: el
        .call('polyglot', 'chat.notify.mentionedYou')
        .replace('{sender}', senderName)
        .replace('{channel}', channelName),
      body: msg?.body?.substring(0, 100) || '',
      // Query form (not hash) — shared/functions/openInSlot.js's route-
      // support check only ever splits a href on '?', so a hash deep link
      // can never resolve to a PageSlot def and always falls back to a full
      // page nav (the exact "mention notifications don't open in the slot"
      // bug this producer was hitting). pages/index.js's auto-pick timer
      // still reads a legacy '#channel=' hash as a back-compat fallback.
      href: `/chat?channel=${row.channel_id}`,
      // CHAT-NOTIFY-1 (tickets/opus.md) — carry the SAME `payload.kind` +
      // `payload.messageId` shape the server's durable notifier writes
      // (workers/workspace-project/src/chatMessageNotify.js), so this
      // client-only, online-only write and the server's fire-and-forget
      // write for the SAME message collide on Notification's partial
      // unique index (core/models/Notification.js) instead of producing two
      // bell rows for one mention — mirrors the credit-threshold client/
      // server dedupe pattern exactly (creditGuard.js ↔
      // creditThresholdNotifier.js).
      metadata: {
        kind: 'chat_message',
        messageId: row.message_id != null ? String(row.message_id) : null,
        channelId: row.channel_id,
        workspaceId: root.workspace?.id || null
      }
    }
  )
}

// Reconcile sidebar-critical state from a `chat.snapshot` (connect/reconnect).
// The server sends service-role-complete rows; merge channels + members so a
// reconnect after a missed-event gap heals without waiting for the page fetch.
// Messages are NOT snapshotted (the page's fetch decls own bulk hydration).
const _handleSnapshot = (root, snap) => {
  if (!snap || typeof snap !== 'object') return
  // The server snapshot has the same workspace ∪ membership reach as the
  // bulk REST lists — scope every slice to the active workspace before it
  // touches state (workspace isolation contract, see chatUtils).
  const activeWs = getActiveWorkspaceId(root)
  if (Array.isArray(snap.channels) && snap.channels.length) {
    const cur = root.channels || []
    const byId = new Map(cur.map((c) => [c.id, c]))
    for (const r of snap.channels) {
      const ch = rowToChannel(r)
      if (!ch.id) continue
      if (activeWs && ch.workspaceId && ch.workspaceId !== activeWs) continue
      byId.set(ch.id, ch)
    }
    root.update({ channels: [...byId.values()] })
  }
  if (Array.isArray(snap.members)) {
    root.update({ channelMembers: scopeMembersToActiveWorkspace(snap.members, root) })
  }
  if (Array.isArray(snap.mentionsUnread)) {
    root.update({ myMentions: snap.mentionsUnread.filter((r) => _chatInScope(root, r)) })
  }
}

// ── Server-SSE transport ────────────────────────────────────────────────────
// Delegates to the SDK's `realtime.subscribeChatSse`, which owns the
// EventSource client + the SSE→(kind, payload) re-framing into the
// `{ eventType, new, old, _kind? }` envelope the shared handler bodies consume.
//
// getService('workspaceProject') is synchronous once the SDK is initialized,
// but the SDK may not be ready at the instant a subscription is requested.
// Resolve defensively and return a deferred unsub (the same pattern
// subscribeMeetRealtime.js uses) so a not-yet-ready SDK doesn't throw.
//
// `deliver(kind, payload)` is the sink: the shared dispatcher. `chat.snapshot`
// is routed separately by the dispatcher.
const _openChatSse = ({ root, deliver, workspaceId }) => {
  let resolvedUnsub = null
  let cancelled = false

  // RESOLVE THE SERVICE, NEVER PROBE IT SYNCHRONOUSLY. `getSDK()` hands back
  // the shell's init-await Proxy (packages/shared/functions/sdkClient.js
  // `wrapWithInitAwait`): SYNC_PASS is only {getAuthToken, isReady, getToken,
  // logout}, so every other function property becomes
  // `(...args) => initPromise.then(() => value.apply(target, args))` and
  // `getService('workspaceProject')` answers a PROMISE. A bare
  // `wp?.realtime?.subscribeChatSse` reads `undefined` on it for ever, so the
  // guard below was false on every call and NO chat stream ever opened —
  // silently, because nothing threw (measured live: 0 EventSource, while
  // `await sdk.getService('workspaceProject')` resolved to the real service
  // with subscribeChatSse a function — CHAT-RT-SSE-PROBE-READS-A-PROMISE-1).
  // The deferred unsub below is what makes the async resolve safe: the caller
  // still gets its handle synchronously.
  Promise.resolve(getSDK()?.getService?.('workspaceProject'))
    .then((wp) => {
      // Unsubscribed while the service was still resolving: never open.
      if (cancelled) return
      const sub = wp?.realtime?.subscribeChatSse
      if (typeof sub !== 'function') return
      const unsub = sub.call(
        wp.realtime,
        {
          workspaceId: workspaceId || undefined
        },
        deliver
      )
      if (cancelled) {
        try {
          unsub?.()
        } catch (_) {}
      } else resolvedUnsub = typeof unsub === 'function' ? unsub : null
    })
    .catch((err) => {
      console.warn('[chat-rt] SSE subscribe failed:', err?.message || err)
    })

  return {
    unsubscribe: () => {
      cancelled = true
      if (typeof resolvedUnsub === 'function') {
        try {
          resolvedUnsub()
        } catch (_) {}
      }
      resolvedUnsub = null
    }
  }
}

export const subscribeRealtime = async function () {
  const root = this.state.root
  const me = root.currentUserEmail

  // SIGNED OUT → NO STREAM. The shell calls this at mount with no session
  // check (workspace app.js); a signed-out shell then opened GET
  // /core/chat/stream and retried it — six 401s on the white-label invite
  // landing (vault.pr, a clean profile, 2026-10-11). Skip when the boot gate
  // decided "signed out" (a stored token the server refused counts), and when
  // there is neither a user nor a token. A signed-in shell whose user has not
  // landed yet still opens it: its token is present
  // (__tests__/chatRealtimeSignedOut.test.js).
  if (this?.context?.bootSignedIn === false) return null
  if (!me) {
    let token = null
    try {
      token = getSDK()?.getAuthToken?.() || null
    } catch (_) {
      token = null
    }
    if (!token) return null
  }

  const activeWs = getActiveWorkspaceId(root)

  // Guard: reuse an existing subscription ONLY while it belongs to the
  // active workspace. On drift (org/workspace switch) the old stream keeps
  // delivering the previous tenant's events — close it and open a fresh
  // one keyed to the new workspace.
  const existing = root._chatRealtimeChannel
  if (existing) {
    if (existing._wsId === activeWs) return existing
    try {
      existing.unsubscribe?.()
    } catch (_) {
      /* already closed */
    }
    root._chatRealtimeChannel = null
  }

  // The state dispatcher — routes a (kind, payload) to the shared handler body.
  // Every entity event is workspace-gated HERE (one choke point): the server
  // SSE stream is server-scoped, so cross-workspace events must never reach the
  // state handlers.
  const dispatch = (kind, payload) => {
    if (kind === 'chat.snapshot') {
      _handleSnapshot(root, payload)
      return
    }
    if (kind === 'chat.channels') {
      const row = payload?.new || payload?.old
      if (!_chatInScope(root, row)) return
      if (payload?._kind === 'member') _handleMembers(root, payload)
      else _handleChannels(root, payload)
      return
    }
    if (kind === 'chat.mentions') {
      if (!_chatInScope(root, payload?.new)) return
      _handleMentionInsert(root, payload, me, this)
      return
    }
    if (kind === 'chat.typing') {
      if (!_chatInScope(root, payload?.new)) return
      _handleTyping(root, payload)
      return
    }
    if (kind === 'chat.messages') {
      const ev = payload?.eventType || 'INSERT'
      // A DELETE frame carries only `old` ({ id, channel_id }) — scope it on
      // that, never on the null `new` (which dropped every delete).
      if (ev === 'DELETE') {
        if (!_chatInScope(root, payload?.old)) return
        _handleMessageDelete(root, payload)
        return
      }
      if (!_chatInScope(root, payload?.new)) return
      if (ev === 'UPDATE') {
        _handleMessageUpdate(root, payload)
        return
      }
      // INSERT (and any non-UPDATE) append-path. `this` is subscribeRealtime's
      // own `this` (dispatch is an arrow fn, inherits it lexically) — the
      // element el.call('subscribeRealtime') was invoked on, threaded through
      // so _notifyNewMessage can el.call('openNotification', …) without a
      // second element reference.
      _handleMessageInsert(root, payload, this)
    }
  }

  // Canonical resolution (chatUtils.getActiveWorkspaceId) — the previous
  // reads (root.activeWorkspaceId / root.workspaceId / root.activeOrganization)
  // were keys nothing ever writes, so the stream always opened unscoped.
  const workspaceId = activeWs || undefined

  const handle = _openChatSse({ root, workspaceId, deliver: dispatch })

  // Key the handle to the workspace it was opened for — the reuse guard
  // above compares this to detect workspace drift and re-open.
  if (handle) handle._wsId = activeWs
  root._chatRealtimeChannel = handle
  return handle
}
